import { useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { resolveBackendUrl } from '@/config/desktopBootstrap';
import { isDesktopOffline } from '@/api/desktopSync';
import { getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

const principal = binding => JSON.stringify([binding?.user?.id, binding?.user?.tenant_id,
  binding?.user?.roles || [binding?.user?.role], binding?.source]);
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
const safeLocal = value => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
      && !url.username && !url.password && url.pathname === '/' && !url.search && !url.hash;
  } catch { return false; }
};

export function useOrderWorkspaceSession() {
  const { sessionBinding: binding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const offline = isDesktopOffline();
  const identity = principal(binding);
  const connected = connection();
  const key = JSON.stringify([revision, identity, connected, offline]);
  const latest = useRef(key); latest.current = key;
  const roles = binding?.user?.roles || [binding?.user?.role];
  const valid = !!binding?.user?.id && !!binding?.user?.tenant_id
    && ['cloud', 'offline'].includes(binding.source) && binding.revision === revision
    && roles.some(role => ['admin', 'designer'].includes(role));
  const current = () => valid && latest.current === key && binding.revision === getActiveSessionRevision()
    && principal(binding) === identity && connection() === connected && isDesktopOffline() === offline
    && resolveBackendUrl(window.location.protocol, window.lvbDesktop, process.env.REACT_APP_BACKEND_URL) !== null
    && `${resolveBackendUrl(window.location.protocol, window.lvbDesktop, process.env.REACT_APP_BACKEND_URL) || ''}/api` === API;
  const capture = (local = false) => {
    if (!current()) return null;
    const localBase = window.lvbDesktop?.localBackendUrl;
    if ((local || offline) && !safeLocal(localBase)) return null;
    const endpoint = local ? `${localBase}/api` : API;
    if (offline && endpoint !== `${localBase}/api`) return null;
    const token = getAuthTokenForRequest(`${endpoint}/orders`);
    if (!token) return null;
    return { endpoint, tenant: binding.user.tenant_id, actor: binding.user.id, offline, admin: roles.includes('admin'),
      config: { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true },
      current: () => current() && token === getAuthTokenForRequest(`${endpoint}/orders`) };
  };
  return { key, valid, offline, isAdmin: roles.includes('admin'), capture };
}

// Read deadlines also settle transports that ignore AbortSignal. Their late
// replies are never adopted; write custody deliberately does not use this race.
export function boundedOrderRead(execute, config, controllers) {
  const controller = new AbortController();
  controllers.add(controller);
  let timer;
  let abort;
  const cancelled = new Promise((_, reject) => {
    abort = () => reject(new Error('Order observation unavailable'));
    controller.signal.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => controller.abort(), 30000);
  });
  return Promise.race([Promise.resolve().then(() => {
    if (controller.signal.aborted) throw new Error('Order observation retired');
    return execute({ ...config, signal: controller.signal });
  }), cancelled]).finally(() => {
    clearTimeout(timer);
    controller.signal.removeEventListener('abort', abort);
    controllers.delete(controller);
  });
}
